// packages/sdk/src/engine/TriggerEngine.ts

import { eventBus } from './EventBus';
import type { TriggerConfig, SessionConfig } from '../types';

const STORAGE_KEY_PREFIX = 'msw_';

// --- Session helpers (localStorage) ---

function getStorageKey(projectId: string, suffix: string): string {
  return `${STORAGE_KEY_PREFIX}${projectId}_${suffix}`;
}

function hasShownBefore(projectId: string): boolean {
  try {
    return localStorage.getItem(getStorageKey(projectId, 'shown')) === '1';
  } catch {
    return false;
  }
}

function markAsShown(projectId: string): void {
  try {
    localStorage.setItem(getStorageKey(projectId, 'shown'), '1');
  } catch {
    // localStorage unavailable — fail silently
  }
}

// --- TriggerEngine ---

export class TriggerEngine {
  private projectId: string;
  private trigger: TriggerConfig;
  private session: SessionConfig;
  private cleanupFns: Array<() => void> = [];
  private fired = false;

  constructor(
    projectId: string,
    trigger: TriggerConfig,
    session: SessionConfig = {}
  ) {
    this.projectId = projectId;
    this.trigger = trigger;
    this.session = session;
  }

  start(): void {
    // Guard: showOnce — if already shown, do nothing
    if (this.session.showOnce && hasShownBefore(this.projectId)) {
      return;
    }

    switch (this.trigger.type) {
      case 'auto':
        this.fire();
        break;

      case 'delay': {
        const ms = this.trigger.delay ?? 3000;
        const timer = setTimeout(() => this.fire(), ms);
        this.cleanupFns.push(() => clearTimeout(timer));
        break;
      }

      case 'manual':
        // Nothing to set up — MetricShift.open() calls fire() directly
        break;

      default:
        break;
    }
  }

  // Called by MetricShift.open() for manual trigger
  fireManual(flowId?: string): void {
    this.fire(flowId);
  }

  private fire(flowId?: string): void {
    if (this.fired) return;
    this.fired = true;

    if (this.session.showOnce) {
      markAsShown(this.projectId);
    }

    eventBus.emit({ type: 'open', payload: { flowId } });
  }

  // Reset fired state — allows re-triggering after close if showOnce is false
  reset(): void {
    this.fired = false;
  }

  // Tear down all listeners and timers
  destroy(): void {
    this.cleanupFns.forEach(fn => fn());
    this.cleanupFns = [];
  }
}